<template>
  <div class="docs">
    <ui-title :title="t('docs.faq.title')" :wave="'/docsTitleBg.png'" />
    <div class="docs__wrapper">
      <docs-navigation
          ref="navRef"
          :list="navigationList"
          @navigate="scrollToSection"
          :active-section="activeSection"
      />
      <el-scrollbar
          ref="scrollbarRef"
          always
          class="docs__main"
          @scroll="handleScroll"
      >
        <div
            v-for="(el, idx) of faqs"
            :key="idx"
            class="docs__main-block"
            :id="`section-${idx}`"
            ref="sectionsRef"
        >
          <h2 class="docs__main-title" v-if="el.title">{{ t(`docs.faq.content[${idx}].title`) }}</h2>
          <i18n-t
              class="docs__main-text"
              v-if="el.text"
              scope="global"
              tag="p"
              :keypath="`docs.faq.content[${idx}].text`"
          >
            <template #domain>
              <router-link :to="translations.i18nRoute({ name: 'home' })">Findprotemplate.com</router-link>
            </template>
            <template #companyName>
              <span>{{ companyName }}</span>
            </template>
            <template #companyAddress>
              <span>{{ companyAddress }}</span>
            </template>
            <template #companyNumber>
              <span>{{ companyNumber }}</span>
            </template>
          </i18n-t>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script setup>
import UiTitle from "@/components/ui/ui-title.vue";
import {useI18n} from "vue-i18n";
import DocsNavigation from "@/components/docs/docs-navigation.vue";
import en from '@/locales/en-gb.json'
import {computed, nextTick, onMounted, onUnmounted, ref} from "vue";
import translations from "@/core/helpers/translations.js";
import {useCompanyStore} from "@/stores/company.js";

const {t} = useI18n()
const companyStore = useCompanyStore()

const faqs = ref(en.docs.faq.content)

const company = computed(() => {
  return companyStore.companyInfo ? companyStore.companyInfo : null
})
const companyAddress = computed(() => {
  return company.value?.companyAddress || '';
});
const companyName = computed(() => {
  if (!company.value?.companyName) return '';
  return company.value?.companyName.split('|')[0].trim();
});
const companyNumber = computed(() => {
  if (!company.value?.companyName) return '';
  return company.value?.companyName.split('|')[1].trim();
});

const navRef = ref(null);
const scrollbarRef = ref(null);
const sectionsRef = ref([]);

const navigationList = [
  {
    id: 0,
    text: 'docs.faq.nav.item1'
  },
  {
    id: 1,
    text: 'docs.faq.nav.item2'
  },
  {
    id: 2,
    text: 'docs.faq.nav.item3'
  },
  {
    id: 3,
    text: 'docs.faq.nav.item4'
  },
  {
    id: 4,
    text: 'docs.faq.nav.item5'
  },
  {
    id: 5,
    text: 'docs.faq.nav.item6'
  },
  {
    id: 6,
    text: 'docs.faq.nav.item7'
  },
  {
    id: 7,
    text: 'docs.faq.nav.item8'
  },
  {
    id: 8,
    text: 'docs.faq.nav.item9'
  }
]

const activeSection = ref(null);

const updateSizes = () => {
  nextTick(() => {
    if (navRef.value && scrollbarRef.value) {
      const navHeight = navRef.value.$el.offsetHeight;
      scrollbarRef.value.$el.style.height = `${navHeight}px`;
    }
  });
};

const handleScroll = () => {
  if (!scrollbarRef.value) return;

  const wrapElement = scrollbarRef.value.$el.querySelector('.el-scrollbar__wrap');
  if (!wrapElement) return;

  const scrollTop = wrapElement.scrollTop;
  const scrollPosition = scrollTop + 50;

  if (!sectionsRef.value.length) return;

  for (let i = 0; i < sectionsRef.value.length; i++) {
    const section = sectionsRef.value[i];
    if (!section) continue;

    const top = section.offsetTop;
    const height = section.offsetHeight;

    if (scrollPosition >= top && scrollPosition < top + height) {
      activeSection.value = i;
      break;
    }
  }
};

const scrollToSection = (id) => {
  if (!scrollbarRef.value || !sectionsRef.value[id]) return;

  const targetTop = sectionsRef.value[id].offsetTop;

  const wrapElement = scrollbarRef.value.$el.querySelector('.el-scrollbar__wrap');
  if (!wrapElement) return;

  wrapElement.scrollTo({
    top: targetTop - 30,
    behavior: 'smooth'
  });

  activeSection.value = id;
};

onMounted(() => {
  updateSizes();
  window.addEventListener('resize', updateSizes);

  nextTick(() => {
    handleScroll();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSizes);
});
</script>

<style lang="scss" scoped>
.docs {
  top: -103px;
  margin-bottom: -110px;

  &__wrapper {
    display: flex;
    align-items: flex-start;
  }

  &__main {
    width: 100%;
    background-color: $white;
    padding: 30px 40px;
    position: relative;
    overflow-y: auto;

    &-block {
      margin-bottom: 30px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &-text {
      color: $black;
      font-family: Syne, sans-serif;
      font-size: 16px;
      font-weight: 700;

      & a {
        transition: 0.2s;
        color: #49123f;

        &:hover {
          color: #772668;
        }
      }
    }

    &-title {
      font-family: Syne, sans-serif;
      font-size: 30px;
      font-weight: 700;
      background: linear-gradient(90deg, #FF85FD 0.5%, #7D045A 44.83%, #3D2B44 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
}

@media (max-width: 800px) {
  .docs__main {
    height: auto !important;
  }
}
</style>